import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export function Payslip() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);

  const companyId = localStorage.getItem("demoCompanyId");

  useEffect(() => {
    if (!companyId || !runId) {
      setError("Fehlende Daten");
      setLoading(false);
      return;
    }

    const loadPayslip = async () => {
      try {
        const res = await apiClient.get(
          `/companies/${companyId}/payroll-runs/${runId}/payslip`
        );
        setData(res.data);
      } catch (e) {
        console.error(e);
        setError(e.response?.data?.detail || "Fehler beim Laden der Lohnabrechnung");
      } finally {
        setLoading(false);
      }
    };

    loadPayslip();
  }, [companyId, runId]);

  const resend = async () => {
    setSending(true);
    try {
      const r = await apiClient.post(`/companies/${companyId}/payroll-runs/${runId}/send-payslip`);
      const d = r.data || {};
      setData((prev) => ({ ...prev, delivery: d }));
      const parts = [
        d.email?.sent ? `E-Mail an ${d.email.to} ✓` : `E-Mail: ${d.email?.error || "nicht gesendet"}`,
        d.whatsapp?.sent ? `WhatsApp an ${d.whatsapp.to} ✓` : `WhatsApp: ${d.whatsapp?.error || "nicht gesendet"}`,
      ];
      (d.email?.sent || d.whatsapp?.sent ? toast.success : toast.error)(parts.join(" · "));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Versand fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="payslip-loading">
        <p className="text-slate-500">Lade Lohnabrechnung...</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex items-center justify-center min-h-screen" data-testid="payslip-error">
        <Card className="p-6 max-w-md">
          <p className="text-red-600 mb-4" data-testid="payslip-error-message">{error || "Keine Daten gefunden"}</p>
          <Button onClick={() => navigate("/payroll")} data-testid="payslip-error-back-button">
            Zurück zur Lohnbuchhaltung
          </Button>
        </Card>
      </div>
    );
  }

  // Periode formatieren (z.B. 2025-01 -> Januar 2025)
  const formatPeriod = (period) => {
    if (!period) return "";
    const [year, month] = period.split("-");
    const months = [
      "Januar", "Februar", "März", "April", "Mai", "Juni",
      "Juli", "August", "September", "Oktober", "November", "Dezember"
    ];
    const monthIndex = parseInt(month, 10) - 1;
    return `${months[monthIndex] || month} ${year}`;
  };

  // Datum formatieren (ISO -> DD.MM.YYYY)
  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      const d = new Date(dateStr);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    } catch {
      return dateStr;
    }
  };

  const fmtCHF = (v) => Number(v || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const rates = data.breakdown?.rates || {};
  const pct = (v) => (v === undefined || v === null ? "" : ` (${Number(v).toFixed(Number.isInteger(Number(v) * 100) ? 2 : 3).replace(/\.?0+$/, "")} %)`);
  const totalDeductions =
    data.social_security + data.pension + data.tax_withholding +
    (data.breakdown?.ausgleich_geldwert || 0) + (data.breakdown?.manual_deduction || 0) + (data.breakdown?.receivable_offset?.amount || 0);

  return (
    <div className="min-h-screen bg-slate-50 py-8 print:bg-white print:py-0" data-testid="payslip-page">
      {/* Nicht-Druck: Zurück-Button und Drucken-Button */}
      <div className="print:hidden max-w-4xl mx-auto px-4 mb-4 flex justify-between items-center">
        <Button variant="outline" onClick={() => navigate("/payroll")} data-testid="payslip-back-button">
          ← Zurück
        </Button>
        <div className="flex items-center gap-2">
          {data.delivery && (
            <span className="text-xs text-slate-600 flex items-center gap-2" data-testid="payslip-delivery-status">
              <span className={data.delivery.email?.sent ? "text-emerald-700" : "text-slate-400"} title={data.delivery.email?.error || data.delivery.email?.to || ""}>
                E-Mail {data.delivery.email?.sent ? "✓" : "–"}
              </span>
              <span className={data.delivery.whatsapp?.sent ? "text-emerald-700" : "text-slate-400"} title={data.delivery.whatsapp?.error || data.delivery.whatsapp?.to || ""}>
                WhatsApp {data.delivery.whatsapp?.sent ? "✓" : "–"}
              </span>
            </span>
          )}
          <Button variant="outline" onClick={resend} disabled={sending} data-testid="payslip-send-button">
            {sending ? "Sende…" : "Per E-Mail + WhatsApp senden"}
          </Button>
          <Button onClick={() => window.print()} className="bg-slate-900 text-white" data-testid="payslip-print-button">
            Drucken / PDF speichern
          </Button>
        </div>
      </div>

      {/* Hauptinhalt: Lohnabrechnung */}
      <div className="max-w-4xl mx-auto bg-white shadow-lg print:shadow-none">
        <Card className="p-8 print:border-none" data-testid="payslip-content">
          {/* Header: Firmendaten mit Logo + Branding */}
          <div
            className="mb-8 pb-6 border-b-2"
            style={{ borderColor: data.company_primary_color || "#0f172a" }}
            data-testid="payslip-company-header"
          >
            <div className="flex items-start gap-4">
              {data.company_logo_data_url && (
                <img
                  src={data.company_logo_data_url}
                  alt={data.company_name}
                  className="max-h-20 max-w-[200px] object-contain"
                  data-testid="payslip-company-logo"
                />
              )}
              <div className="flex-1">
                <h1
                  className="text-2xl font-bold mb-2"
                  style={{ color: data.company_primary_color || "#0f172a" }}
                  data-testid="payslip-company-name"
                >
                  {data.company_name}
                </h1>
                {data.company_address && (
                  <p className="text-sm text-slate-600" data-testid="payslip-company-address">{data.company_address}</p>
                )}
                {data.company_city && (
                  <p className="text-sm text-slate-600" data-testid="payslip-company-city">{data.company_city}</p>
                )}
                {(data.company_phone || data.company_email || data.company_website) && (
                  <p className="text-xs text-slate-500 mt-1" data-testid="payslip-company-contact">
                    {[data.company_phone, data.company_email, data.company_website].filter(Boolean).join(" · ")}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Titel */}
          <div className="mb-6 text-center">
            <h2
              className="text-xl font-semibold"
              style={{ color: data.company_primary_color || "#0f172a" }}
            >
              {data.run_type === "correction" ? "Lohnkorrektur" : "Lohnabrechnung"}
            </h2>
            <p className="text-sm text-slate-600 mt-1" data-testid="payslip-period">{formatPeriod(data.period)}</p>
          </div>

          {data.offset_lines?.length > 0 && (
            <div className="mb-6 rounded-md border border-teal-300 bg-teal-50 px-4 py-3 text-sm text-teal-900 space-y-0.5" data-testid="payslip-offset-box">
              {data.offset_lines.map((l, i) => (
                <p key={i} className={i === 0 ? "font-semibold" : ""} data-testid={`payslip-offset-line-${i}`}>{l}</p>
              ))}
            </div>
          )}
          {data.expense_lines?.length > 0 && (
            <div className="mb-6 rounded-md border border-cyan-300 bg-cyan-50 px-4 py-3 text-sm text-cyan-900 space-y-0.5" data-testid="payslip-expense-box">
              {data.expense_lines.map((l, i) => (
                <p key={i} className={i === 0 ? "font-semibold" : "text-xs"} data-testid={`payslip-expense-line-${i}`}>{l}</p>
              ))}
            </div>
          )}
          {data.correction_lines?.length > 0 && (
            <div className="mb-6 rounded-md border border-orange-300 bg-orange-50 px-4 py-3 text-sm text-orange-900 space-y-0.5" data-testid="payslip-correction-box">
              {data.correction_lines.map((l, i) => (
                <p key={i} className={i === 0 ? "font-semibold" : ""} data-testid={`payslip-correction-line-${i}`}>{l}</p>
              ))}
              {data.correction?.original_run_id && (
                <a href={`/payslip/${data.correction.original_run_id}`} target="_blank" rel="noreferrer" className="underline text-orange-800 text-xs" data-testid="payslip-correction-original-link">
                  Ursprüngliche Lohnabrechnung öffnen
                </a>
              )}
            </div>
          )}

          {/* Mitarbeiterdaten */}
          <div className="mb-6 grid grid-cols-2 gap-6" data-testid="payslip-employee-section">
            <div>
              <h3 className="text-sm font-medium text-slate-700 mb-2">Mitarbeiter</h3>
              <p className="text-sm text-slate-900 font-medium" data-testid="payslip-employee-name">
                {data.employee_first_name} {data.employee_last_name}
              </p>
              {data.employee_address && (
                <p className="text-sm text-slate-600" data-testid="payslip-employee-address">{data.employee_address}</p>
              )}
              {data.employee_city && (
                <p className="text-sm text-slate-600" data-testid="payslip-employee-city">{data.employee_city}</p>
              )}
              {data.employee_ahv_number && (
                <p className="text-sm text-slate-600 mt-2" data-testid="payslip-employee-ahv">
                  AHV-Nr.: {data.employee_ahv_number}
                </p>
              )}
            </div>
            <div className="text-right">
              <h3 className="text-sm font-medium text-slate-700 mb-2">Lohnperiode {formatPeriod(data.period)}</h3>
              <p className="text-sm text-slate-900" data-testid="payslip-payment-date">Datum: {formatDate(data.payment_date)}</p>
              {data.employee_number && <p className="text-sm text-slate-600">Mitarbeiter-Nr.: {data.employee_number}</p>}
            </div>
          </div>

          {/* Lohnbestandteile – Schweizer Standard: Lohnart · Bezeichnung · Basis · Ansatz · Betrag */}
          <div className="mb-6" data-testid="payslip-salary-table">
            {data.rows ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-white" style={{ backgroundColor: data.company_primary_color || "#0f172a" }}>
                    <th className="text-left py-2 px-2 font-semibold w-16">Lohnart</th>
                    <th className="text-left py-2 px-2 font-semibold">Bezeichnung</th>
                    <th className="text-right py-2 px-2 font-semibold w-32">Basis</th>
                    <th className="text-right py-2 px-2 font-semibold w-20">Ansatz</th>
                    <th className="text-right py-2 px-2 font-semibold w-32">Betrag in CHF</th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => {
                    const strong = ["total", "total_deduction", "net", "payout"].includes(r.kind);
                    const neg = r.kind === "deduction" || (r.kind === "total_deduction" && r.amount > 0);
                    const negValue = !neg && r.amount < 0;
                    const base = typeof r.base === "string" ? r.base : (r.base ? `${fmtCHF(r.base)} CHF` : "");
                    return (
                      <tr key={r.code + r.label} className={`border-b border-slate-100 ${strong ? "font-semibold border-t border-slate-300" : ""} ${r.kind === "net" ? "bg-emerald-50" : ""}`} data-testid={`payslip-row-${r.code}`}>
                        <td className="py-1.5 px-2 text-slate-500 font-mono text-xs">{r.code}</td>
                        <td className="py-1.5 px-2 text-slate-800">{r.label}</td>
                        <td className="py-1.5 px-2 text-right text-slate-600 font-mono text-xs">{base}</td>
                        <td className="py-1.5 px-2 text-right text-slate-600 font-mono text-xs">{r.rate || ""}</td>
                        <td className={`py-1.5 px-2 text-right font-mono ${neg || negValue ? "text-red-700" : "text-slate-900"}`}>{neg ? "-" : ""}{fmtCHF(r.amount)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <p className="text-sm text-slate-500">Keine Detaildaten vorhanden.</p>
            )}
          </div>

          {/* Auszahlung */}
          <div className="mb-6 rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm" data-testid="payslip-payout-section">
            <h3 className="font-semibold text-slate-800 mb-1">Information zur Auszahlung</h3>
            <p className="text-slate-600">Der Betrag wurde wie folgt ausbezahlt:</p>
            {data.payout?.iban ? (
              <>
                <p className="text-slate-700 mt-1">Bank: <span className="font-medium">{data.payout.bank_name || "Banküberweisung"}</span></p>
                <p className="text-slate-700">IBAN / Konto: <span className="font-mono">{data.payout.iban}</span></p>
              </>
            ) : (
              <p className="text-amber-700 mt-1">Bank/IBAN noch nicht hinterlegt – unter Mitarbeitende → Bearbeiten ergänzen (oder via Onboarding durch den Mitarbeiter).</p>
            )}
            <p className="text-slate-700 mt-1">Valuta: {formatDate(data.payment_date)} · Gesamtauszahlungsbetrag: <span className="font-semibold font-mono">CHF {fmtCHF(data.net_salary)}</span></p>
          </div>

          {/* Ferienkonto (Monatslöhner) */}
          {data.vacation && (
            <div className="mb-6 rounded-lg border border-slate-200 p-4 text-sm" data-testid="payslip-vacation-section">
              <div className="flex items-baseline justify-between mb-2">
                <h3 className="font-semibold text-slate-800">Ferienkonto {data.vacation.year}</h3>
                {data.vacation.note && <span className="text-xs text-slate-500">{data.vacation.note}</span>}
              </div>
              <div className="grid grid-cols-4 gap-3 text-center">
                {[["Anspruch", data.vacation.entitlement, "vacation-entitlement"], ["bezogen", data.vacation.taken, "vacation-taken"],
                  ["diesen Monat", data.vacation.taken_in_period, "vacation-period"], ["Restanspruch", data.vacation.remaining, "vacation-remaining"]].map(([label, v, tid]) => (
                  <div key={tid} className={`rounded-md py-2 ${tid === "vacation-remaining" ? "bg-emerald-50" : "bg-slate-50"}`} data-testid={`payslip-${tid}`}>
                    <div className="text-[11px] uppercase tracking-wide text-slate-500">{label}</div>
                    <div className={`font-mono text-lg ${tid === "vacation-remaining" ? "font-semibold text-emerald-800" : "text-slate-800"}`}>{Number(v).toLocaleString("de-CH")} Tage</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Arbeitgeber-Beiträge (Info) */}
          <div className="mt-8 pt-6 border-t border-slate-200 bg-slate-50 -mx-8 px-8 py-4 print:bg-slate-50" data-testid="payslip-employer-section">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">
              Arbeitgeberbeiträge (Information)
            </h3>
            <p className="text-xs text-slate-600 mb-3">
              Diese Beträge werden vom Arbeitgeber zusätzlich zum Bruttolohn bezahlt und nicht vom Lohn abgezogen.
            </p>
            <div className="space-y-1 text-sm">
              {data.breakdown ? (
                <>
                  {[["AHV/IV/EO", data.breakdown.ahv_employer, rates.ahv_employer],
                    ["ALV", data.breakdown.alv_employer, rates.alv_employer],
                    [`FAK Familienausgleichskasse ${data.breakdown.fak_canton || ""}`, data.breakdown.fak_employer, rates.fak_employer],
                    ["UVG/KTG (Arbeitgeber)", data.breakdown.insurance_employer, rates.insurance_employer],
                    ["BVG Pensionskasse", data.breakdown.bvg_employer, rates.bvg_employer]].filter(([, v]) => Number(v) > 0).map(([label, v, r]) => (
                    <div className="flex justify-between" key={label} data-testid={`payslip-employer-${label.slice(0, 3)}`}>
                      <span className="text-slate-700">{label}{pct(r)}:</span>
                      <span className="text-slate-900 font-mono">{Number(v).toFixed(2)} CHF</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-700">AHV/IV/EO/ALV (Arbeitgeber):</span>
                    <span className="text-slate-900 font-mono">{data.employer_social_cost.toFixed(2)} CHF</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-700">Pensionskasse (Arbeitgeber):</span>
                    <span className="text-slate-900 font-mono">{data.employer_pension_cost.toFixed(2)} CHF</span>
                  </div>
                </>
              )}
              <div className="flex justify-between font-medium pt-2 border-t border-slate-300 mt-2">
                <span className="text-slate-900">Gesamtkosten für Arbeitgeber:</span>
                <span className="text-slate-900 font-mono">
                  {data.total_company_cost.toFixed(2)} CHF
                </span>
              </div>
            </div>
          </div>

          {/* Footer mit optionalem Branding-Footer */}
          <div className="mt-8 pt-4 border-t border-slate-200 text-xs text-slate-500 text-center">
            {data.company_invoice_footer && (
              <p className="mb-2 whitespace-pre-line" data-testid="payslip-branding-footer">
                {data.company_invoice_footer}
              </p>
            )}
            <p>Diese Lohnabrechnung wurde automatisch erstellt.</p>
          </div>
        </Card>
      </div>
    </div>
  );
}
